/** Single source of truth for Datadog custom action names and their attribute contracts. */
export const TELEMETRY_EVENTS = {
  // Session
  UserLogin: 'user_login',
  UserLogout: 'user_logout',
  SessionStarted: 'session_started',
  SessionEnded: 'session_ended',

  // Access control
  PlatformAccessDenied: 'platform_access_denied',

  // Dashboard layout
  ModuleClicked: 'module_clicked',
  DashboardEditModeEntered: 'dashboard_edit_mode_entered',
  DashboardLayoutSaved: 'dashboard_layout_saved',
  DashboardLayoutCancelled: 'dashboard_layout_cancelled',
  DashboardModuleReordered: 'dashboard_module_reordered',

  // Quick Access
  QuickAccessLinkClicked: 'quick_access_link_clicked',
  QuickAccessFavouriteAdded: 'quick_access_favourite_added',
  QuickAccessFavouriteRemoved: 'quick_access_favourite_removed',
  QuickAccessAllLinksOpened: 'quick_access_all_links_opened',

  // News
  NewsOpened: 'news_opened',
  NewsTabChanged: 'news_tab_changed',

  // Learning
  LearningCardClicked: 'learning_card_clicked',

  // Career
  CareerCardClicked: 'career_card_clicked',

  // HR Resources
  HrResourceClicked: 'hr_resource_clicked',

  // Active Discussions
  DiscussionCommunityClicked: 'discussion_community_clicked',
  DiscussionPostClicked: 'discussion_post_clicked',

  // ADB Spotlight
  SpotlightVideoOpened: 'spotlight_video_opened',
  SpotlightVideoClosed: 'spotlight_video_closed',

  // Upcoming Events
  EventSavedToCalendar: 'event_saved_to_calendar',
  EventFeaturedModalOpened: 'event_featured_modal_opened',

  // Keyword Search
  SearchQuerySubmitted: 'search_query_submitted',
  KeywordSearchResultClicked: 'keyword_search_result_clicked',
  KeywordSearchCategoryTabClicked: 'keyword_search_category_tab_clicked',
  SearchFilterPanelToggled: 'search_filter_panel_toggled',
  SearchFilterApplied: 'search_filter_applied',
  SearchFilterCleared: 'search_filter_cleared',
  SearchFilterRemoved: 'search_filter_removed',
  SearchSortChanged: 'search_sort_changed',
  SearchDateFilterApplied: 'search_date_filter_applied',

  // AI Chat
  AskAiQuerySubmitted: 'ask_ai_query_submit',
  AskAiResponseCancelled: 'ask_ai_response_cancelled',
  AskAiMessageRegenerated: 'ask_ai_message_regenerated',
  AskAiFeedbackGiven: 'ask_ai_feedback_given',
  AskAiResponseCopied: 'ask_ai_response_copied',
  AskAiChatDownloaded: 'ask_ai_chat_downloaded',
  AskAiCitationClicked: 'ask_ai_citation_clicked',
  AskAiRelatedQuerySelected: 'ask_ai_related_query_selected',

  // People Finder
  PeopleSearchSubmitted: 'people_search_submitted',
  PeopleProfileClicked: 'people_profile_clicked',
  PeopleFilterApplied: 'people_filter_applied',
} as const;

export type TelemetryEventName = (typeof TELEMETRY_EVENTS)[keyof typeof TELEMETRY_EVENTS];

export interface TelemetryEventAttributes {
  // Session
  [TELEMETRY_EVENTS.UserLogin]: Record<string, never>;
  [TELEMETRY_EVENTS.UserLogout]: Record<string, never>;
  [TELEMETRY_EVENTS.SessionStarted]: Record<string, never>;
  [TELEMETRY_EVENTS.SessionEnded]: Record<string, never>;

  // Access control
  [TELEMETRY_EVENTS.PlatformAccessDenied]: {
    reason: 'no-account' | 'not-member' | 'graph-error' | 'group-not-configured';
  };

  // Dashboard layout
  [TELEMETRY_EVENTS.ModuleClicked]: { module_name: string };
  [TELEMETRY_EVENTS.DashboardEditModeEntered]: Record<string, never>;
  [TELEMETRY_EVENTS.DashboardLayoutSaved]: Record<string, never>;
  [TELEMETRY_EVENTS.DashboardLayoutCancelled]: Record<string, never>;
  [TELEMETRY_EVENTS.DashboardModuleReordered]: { module_name: string };

  // Quick Access
  [TELEMETRY_EVENTS.QuickAccessLinkClicked]: { service_link: string; link_title: string };
  [TELEMETRY_EVENTS.QuickAccessFavouriteAdded]: { service_link: string; link_title: string };
  [TELEMETRY_EVENTS.QuickAccessFavouriteRemoved]: { service_link: string; link_title: string };
  [TELEMETRY_EVENTS.QuickAccessAllLinksOpened]: Record<string, never>;

  // News
  [TELEMETRY_EVENTS.NewsOpened]: { news_id: string; tab_name: string; news_title: string };
  [TELEMETRY_EVENTS.NewsTabChanged]: { tab_name: string };

  // Learning
  [TELEMETRY_EVENTS.LearningCardClicked]: { learning_id: string; learning_title: string };

  // Career
  [TELEMETRY_EVENTS.CareerCardClicked]: { career_id: string; career_title: string };

  // HR Resources
  [TELEMETRY_EVENTS.HrResourceClicked]: { resource_id: string; resource_title: string };

  // Active Discussions
  [TELEMETRY_EVENTS.DiscussionCommunityClicked]: { community_id: string; community_name: string };
  [TELEMETRY_EVENTS.DiscussionPostClicked]: {
    post_id: string;
    post_title: string;
    community_id: string;
    community_name: string;
  };

  // ADB Spotlight
  [TELEMETRY_EVENTS.SpotlightVideoOpened]: { video_id: string; video_title: string };
  [TELEMETRY_EVENTS.SpotlightVideoClosed]: { video_id: string };

  // Upcoming Events
  [TELEMETRY_EVENTS.EventSavedToCalendar]: { event_id: string; event_title: string };
  [TELEMETRY_EVENTS.EventFeaturedModalOpened]: { event_id: string; event_title: string };

  // Keyword Search
  [TELEMETRY_EVENTS.SearchQuerySubmitted]: { search_query: string };
  [TELEMETRY_EVENTS.KeywordSearchResultClicked]: {
    search_keyword: string;
    result_id: string;
    result_position: number;
  };
  [TELEMETRY_EVENTS.KeywordSearchCategoryTabClicked]: { search_keyword: string; tab_name: string };
  [TELEMETRY_EVENTS.SearchFilterPanelToggled]: { state: 'open' | 'close' };
  [TELEMETRY_EVENTS.SearchFilterApplied]: { filter_type: string; filter_value: string };
  [TELEMETRY_EVENTS.SearchFilterCleared]: Record<string, never>;
  [TELEMETRY_EVENTS.SearchFilterRemoved]: { filter_type: string; filter_value: string };
  [TELEMETRY_EVENTS.SearchSortChanged]: { sort_option: string };
  [TELEMETRY_EVENTS.SearchDateFilterApplied]: {
    preset?: string;
    date_from?: string;
    date_to?: string;
  };

  // AI Chat
  [TELEMETRY_EVENTS.AskAiQuerySubmitted]: { search_query: string };
  [TELEMETRY_EVENTS.AskAiResponseCancelled]: Record<string, never>;
  [TELEMETRY_EVENTS.AskAiMessageRegenerated]: { message_index: number };
  [TELEMETRY_EVENTS.AskAiFeedbackGiven]: {
    message_index: number;
    feedback_type: 'thumbs_up' | 'thumbs_down';
  };
  [TELEMETRY_EVENTS.AskAiResponseCopied]: Record<string, never>;
  [TELEMETRY_EVENTS.AskAiChatDownloaded]: Record<string, never>;
  [TELEMETRY_EVENTS.AskAiCitationClicked]: { citation_url: string };
  [TELEMETRY_EVENTS.AskAiRelatedQuerySelected]: { query: string };

  // People Finder
  [TELEMETRY_EVENTS.PeopleSearchSubmitted]: { search_query: string };
  [TELEMETRY_EVENTS.PeopleProfileClicked]: { person_id: string };
  [TELEMETRY_EVENTS.PeopleFilterApplied]: { filter_type: string; filter_value: string };
}
